// @ts-strict-ignore
import React, { ComponentProps, useContext, useState } from 'react';
import styled from 'styled-components';

import { FormBuilderBlockData } from 'api/cms';
import { Box } from 'components/Box';
import { LegacyPrimaryButton as PrimaryButton } from 'components/Button/LegacyPrimaryButton';
import { Label } from 'components/Form/Field';
import { BodyLarge, RichTextHeadline } from 'components/Text';
import { TabContext } from 'containers/TabContext';
import { colors } from 'theme';
import { trackEvent } from 'utils/analytics';
import { imageUrl } from 'utils/filestack';

import { FormBuilderBlockContainer } from './FormBuilderBlockContainer';
import { FormBuilderBlockField } from './FormBuilderBlockField';
import { getFormBuilderId } from './getFormBuilderId';

const getImageUrl = (handle: string, mimetype: string) =>
  imageUrl({
    handle,
    mimetype,
    transform: `resize=width:2096,fit:max/quality=value:70`,
  });

const StyledLabel = styled(Label)`
  color: white;
  margin-bottom: 6px;
`;

const SubmitButton = styled(
  ({
    isSidebar,
    ...props
  }: Omit<ComponentProps<typeof PrimaryButton>, 'isSidebar'>) => (
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line react/jsx-props-no-spreading
    <PrimaryButton {...props} />
  )
)`
  padding-left: 40px;
  padding-right: 40px;
  max-width: 440px;
  box-shadow: 0 0 0 1px white inset;
  background: none;
  color: white;
  &:focus,
  &:hover {
    box-shadow: 0 0 0 1px ${colors.turtleGreen} inset;
    background: ${colors.turtleGreen};
  }
  ${(props) =>
    props.isSidebar &&
    `
    width: 100%;
  `}
`;

type Props = {
  data: FormBuilderBlockData;
};

export function FormBuilderBlock({
  data: { headline, submitButtonText, bgImage, action, newTab, fields, method },
}: Props) {
  const isTabbing = useContext(TabContext);
  const [submitted, setSubmitted] = useState(false);

  const onSubmit = () => {
    setSubmitted(true);
    trackEvent('CMS Form Submitted', {
      action,
    });
  };

  return (
    <div>
      <Box
        p="80px 20px"
        width="auto !important"
        borderRadius={[0, 0, 0, 24]}
        background={`url("${
          bgImage
            ? getImageUrl(bgImage.handle, bgImage.mimetype)
            : '/static/images/blue-loops.svg'
        }") center/cover`}
      >
        {headline && (
          <Box maxWidth="960px" m="0 auto">
            <RichTextHeadline color="white" textAlign="center">
              {headline}
            </RichTextHeadline>
          </Box>
        )}

        <Box position="relative">
          <Box
            position="absolute"
            top="50%"
            left="50%"
            transform="translate(-50%, -50%)"
            display={!submitted && 'none'}
            width={1}
          >
            <BodyLarge color="white" textAlign="center">
              {getText('Thank you!')}
            </BodyLarge>
          </Box>

          <Box visibility={submitted && 'hidden'}>
            <FormBuilderBlockContainer $isTabbing={isTabbing}>
              <form
                action={action}
                target={newTab ? '_blank' : undefined}
                onSubmit={onSubmit}
                method={method}
              >
                <Box maxWidth="440px" m="40px auto 0">
                  {fields.map((field) => (
                    <Box
                      my={field.type === 'CHECKBOX' ? 24 : 16}
                      key={field.name}
                    >
                      {!['CHECKBOX', 'HIDDEN'].includes(field.type) && (
                        <StyledLabel htmlFor={getFormBuilderId(field.name)}>
                          {field.label}
                        </StyledLabel>
                      )}
                      {/* TODO: Fix this the next time the file is edited. */}
                      {/* eslint-disable-next-line react/jsx-props-no-spreading */}
                      <FormBuilderBlockField {...field} action={action} />
                    </Box>
                  ))}
                  {/* {hiddenFields.map(({ name, value }) => (
                  <input type="hidden" name={name} value={value} key={name} />
                  ))} */}
                  <Box classes={['flex', 'justify-center']} mt="40px">
                    <SubmitButton size="large" type="submit">
                      {submitButtonText}
                    </SubmitButton>
                  </Box>
                </Box>
                <div
                  style={{
                    position: 'absolute',
                    left: -9999,
                    top: -9999,
                  }}
                >
                  <label htmlFor="pardot_extra_field">
                    {getText(`Comments (No Use)`)}
                  </label>
                  <input
                    type="text"
                    id="pardot_extra_field"
                    name="pardot_extra_field"
                  />
                </div>
              </form>
            </FormBuilderBlockContainer>
          </Box>
        </Box>
      </Box>
    </div>
  );
}
